import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ShoppingCart } from "lucide-react";

interface ProductCardProps {
  name: string;
  game: string;
  description: string;
  badge?: string;
  imageSrc: string;
  billgangProductId: string;
  billgangDomain: string;
  pricing: {
    daily: string;
    weekly: string;
    monthly: string;
  };
}

declare global {
  interface Window {
    BillGang?: {
      initialize: () => void;
    };
  }
}

export const ProductCard = ({ 
  name, 
  game, 
  description, 
  badge, 
  imageSrc, 
  pricing,
  billgangProductId,
  billgangDomain 
}: ProductCardProps) => {
  const [selectedDuration, setSelectedDuration] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  useEffect(() => {
    // Initialize Billgang embed when component mounts
    if (window.BillGang) {
      window.BillGang.initialize();
    }
  }, []);

  const pricingOptions = [
    { value: 'daily', label: '24H', price: pricing.daily },
    { value: 'weekly', label: '7D', price: pricing.weekly },
    { value: 'monthly', label: '30D', price: pricing.monthly },
  ];

  return (
    <div className="relative overflow-hidden rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-300 hover:translate-y-[-5px] hover:bg-white/[0.15]">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img 
          src={imageSrc} 
          alt={name}
          className="object-cover w-full h-full"
        />
        {badge && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-purple-vivid/90 text-white text-xs font-medium rounded-full">
            {badge}
          </div>
        )}
      </div>
      
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xl font-bold text-white">{name}</h3>
          <span className="text-sm font-medium text-purple-light/70">{game}</span>
        </div>
        
        <p className="text-purple-light/90 text-sm mb-6">
          {description}
        </p>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-2 p-1 bg-white/5 rounded-lg border border-white/10">
            {pricingOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedDuration(option.value as 'daily' | 'weekly' | 'monthly')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  selectedDuration === option.value
                    ? 'bg-purple-vivid text-white'
                    : 'text-purple-light/70 hover:text-white hover:bg-white/5'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-white">
              <span className="text-2xl font-bold">{pricingOptions.find(o => o.value === selectedDuration)?.price}</span>
            </div>
            <button 
              data-billgang-product-path={`${billgangProductId}-${selectedDuration}`}
              data-billgang-domain={`https://${billgangDomain}`}
              className="inline-flex items-center px-3 py-2 text-sm font-medium bg-purple-vivid hover:bg-purple-vivid/90 text-white rounded-lg transition-colors"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Purchase
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
